<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
-->

<template>
  <div class="interactive-search-form">
    <v-snackbar
      top
      :timeout="10000"
      :color="toasterMessage.mode"
      :value="toasterMessage.text"
      ref="testSnackBar"
    >
      <span v-html="toasterMessage.text" />

      <a @click.stop="toasterMessage.text = null"> Close </a>
    </v-snackbar>
    <IntentOfUseDialog
      :show-dialog="showIntentOfUse"
      :closeable="true"
      @close-intent-of-use="
        event => {
          if (event) {
            showIntentOfUse = false
          }
        }
      "
      @action="
        showIntentOfUse = false
        runSearch()
      "
      @set-toaster="
        text => {
          toasterMessage.text = text
        }
      "
    ></IntentOfUseDialog>
    <h2 class="form-title text-xs-center text-sm-left">
      Search for an individual or company by name or NPI:
    </h2>

    <v-form ref="form">
      <div class="form-group">
        <label>I want to search by:</label>
        <v-btn-toggle mandatory v-model="searchBy">
          <v-btn depressed value="byName"> Name </v-btn>
          <v-btn depressed value="byNPI"> NPI </v-btn>
        </v-btn-toggle>
      </div>

      <section class="by-name" v-if="searchBy === 'byName'">
        <div class="form-group">
          <label>I want to search for:</label>
          <v-btn-toggle mandatory v-model="nameSearchBy">
            <v-btn depressed value="byPerson">
              <v-icon left class="hidden-xs-only">person</v-icon>
              Individual
            </v-btn>
            <v-btn depressed value="byCompany">
              <v-icon left class="hidden-xs-only">business</v-icon>
              Business
            </v-btn>
          </v-btn-toggle>
        </div>

        <section class="by-name-person" v-if="nameSearchBy === 'byPerson'">
          <v-layout row wrap>
            <v-flex xs9 sm7>
              <v-text-field
                label="First Name"
                required
                :rules="[requiredRule]"
                v-model.trim="byPersonSearch.firstName"
              />
            </v-flex>
          </v-layout>
          <v-layout row wrap v-if="nameSearchBy === 'byPerson'">
            <v-flex xs9 sm7>
              <v-text-field
                label="Last Name"
                required
                :rules="[requiredRule]"
                v-model.trim="byPersonSearch.lastName"
              />
            </v-flex>
          </v-layout>
        </section>
        <section class="by-name-company" v-else>
          <v-layout row wrap>
            <v-flex xs9 sm7>
              <v-text-field
                label="Business Name"
                required
                :rules="[requiredRule]"
                v-model.trim="byCompanySearch.companyName"
              />
            </v-flex>
          </v-layout>
        </section>
      </section>

      <section v-else>
        <v-layout row wrap>
          <v-flex xs9 sm7>
            <v-text-field
              label="NPI"
              required
              :rules="[requiredRule, npiRule]"
              :validate-on-blur="true"
              v-model.trim="npi"
            />
          </v-flex>
        </v-layout>
      </section>

      <v-alert
        outline
        type="error"
        class="form-error"
        :value="firstErrorMessage"
      >
        {{ firstErrorMessage }}
      </v-alert>

      <v-layout column align-center>
        <v-btn
          class="search-btn"
          color="primary"
          @click.native="validateForm()"
        >
          Search
        </v-btn>
      </v-layout>
    </v-form>
  </div>
</template>

<script>
import axios from "axios"
import redirect from "@/service/redirect"
import { requiredRule, npiRule } from "@/common/constants"
import IntentOfUseDialog from "@/components/IntentOfUseDialog"

const baseEndpoint = "/services/interactivesearch"
const interactiveResultPage = "/interactive/results"
const npiResultPage = "/npi/results"
const eligibleFlag = "GRANTED"
const npiMatchProp = "npi-match"
const defaultSearchParam = {
  matchingAlgorithm: "Exact"
}
const defaultErrorMessages = {
  MaxMatchesExceeded:
    "Wow! Your search returned more than 100 results. " +
    "Please use the full name of the person or company you are searching for so we can bring you more relevant results."
}

export default {
  name: "InteractiveSearchForm",
  data() {
    return {
      searchBy: "byName",
      nameSearchBy: "byPerson",
      npi: "",
      byPersonSearch: {
        firstName: "",
        lastName: ""
      },
      byCompanySearch: {
        companyName: ""
      },
      requiredRule,
      npiRule,
      remoteErrors: {},
      showIntentOfUse: false,
      toasterMessage: {
        text: null,
        mode: "error"
      }
    }
  },
  components: {
    IntentOfUseDialog
  },
  computed: {
    firstErrorMessage: function() {
      const keys = Object.keys(this.remoteErrors)

      return keys.length ? this.remoteErrors[keys[0]] : null
    }
  },
  methods: {
    validateForm() {
      if (!this.$refs.form.validate()) return
      this.toasterMessage.text = null
      this.showIntentOfUse = true
    },
    runSearch() {
      this.remoteErrors = {}
      axios.get(`${baseEndpoint}/eligibility`).then(response => {
        if (response.data && eligibleFlag === response.data.name) {
          this.search()
        } else if (response.data) {
          this.displayError(response.data)
        }
      })
    },
    getResult(endpoint, params = {}, resultPage, respInterceptor) {
      const _axios = axios.create()

      respInterceptor && _axios.interceptors.response.use(respInterceptor)

      return _axios
        .get(endpoint, { params })
        .then(response => redirect(resultPage, response.data))
        .catch(error => {
          if (error.response.data) {
            this.displayError(error.response.data)
          }
        })
    },
    displayError(message) {
      this.$set(
        this.remoteErrors,
        message.name,
        message.text || defaultErrorMessages[message.name] || "Error occurred."
      )
    },
    search() {
      if (this.searchBy === "byNPI") {
        this.getResult(
          `${baseEndpoint}/npi/${this.npi}`,
          null,
          npiResultPage,
          response => {
            if (response.data && !response.data[npiMatchProp]) {
              // TODO do we need to polyfill promise?: https://babeljs.io/docs/usage/polyfill/
              return Promise.reject({
                response: {
                  data: {
                    name: "NPINotFound",
                    text:
                      "Sorry... that NPI number does not exist in the NPPES Database."
                  }
                }
              })
            } else return response
          }
        )
      } else {
        const params = Object.assign(
          {},
          defaultSearchParam,
          this[`${this.nameSearchBy}Search`]
        )

        this.getResult(`${baseEndpoint}/name`, params, interactiveResultPage)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/InteractiveSearchForm.vue